/**
 * The dashboard's composer: the chat `Composer` with mentions on but no
 * thread behind it. Submitting hands the text to `onSend` (which creates the
 * thread and opens the workspace), so there is never a transcript here. The
 * model picker writes the remembered pick, which the new thread starts from;
 * the research sources picked with the magnifier ride along as metadata.
 */
import { useState, type FC } from "react";

import { PromptInputProvider } from "@/components/ai-elements/prompt-input";
import {
  readModelPicks,
  writeModelPicks,
  type AgentMessageMetadata,
  type ModelPicks
} from "@/features/agents/useAgentChat";
import { useAgentSpecs } from "@/features/agents/useAgentSpecs";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import { Composer } from "@/features/caseIde/chat/components/Composer";
import type { ResearchSourceId } from "@/features/research";
import { cn } from "@/lib/utils";

type Props = {
  /** What `@Agent` addresses and the placeholder names. */
  agentLabel: string;
  onSend: (text: string, metadata?: AgentMessageMetadata) => void;
  /** A thread is being created; the input is held. */
  isSending?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** Where the unsent text is kept for this tab; omit to keep nothing. */
  draftKey?: string;
};

export const DraftComposer: FC<Props> = ({
  agentLabel,
  onSend,
  isSending = false,
  placeholder,
  autoFocus,
  className,
  draftKey
}) => {
  const { members, me } = useTeamMembers();
  const { models, autoModes } = useAgentSpecs();
  const [picks, setPicksState] = useState<ModelPicks>(readModelPicks);
  const setPicks = (patch: Partial<ModelPicks>) => {
    const next = { ...picks, ...patch };
    setPicksState(next);
    writeModelPicks(next);
  };
  const [researchSources, setResearchSources] = useState<ResearchSourceId[]>(
    []
  );
  const submit = (text: string) => {
    if (!researchSources.length) {
      onSend(text);
      return;
    }
    setResearchSources([]);
    onSend(text, { research_sources: researchSources });
  };
  return (
    <div className={cn("w-full", className)} data-testid="draft-composer">
      <PromptInputProvider>
        <Composer
          draftKey={draftKey}
          status={isSending ? "submitted" : "ready"}
          disabled={isSending}
          onSend={submit}
          // No thread yet, so there is nothing to queue behind: while the
          // thread is being created the input is disabled, and otherwise a
          // submit is a send.
          onQueue={submit}
          onStop={() => undefined}
          mode="agent"
          onModeChange={() => undefined}
          showModeSelect={false}
          models={models}
          autoModes={autoModes}
          model={picks.model}
          thinking={picks.thinking}
          optimizeFor={picks.optimizeFor}
          onModelChange={(model) => setPicks({ model })}
          onThinkingChange={(thinking) => setPicks({ thinking })}
          onOptimizeForChange={(optimizeFor) => setPicks({ optimizeFor })}
          agentLabel={agentLabel}
          teamMembers={members}
          meUid={me.uid || null}
          placeholder={placeholder}
          autoFocus={autoFocus}
          researchSources={researchSources}
          onResearchSourcesChange={setResearchSources}
        />
      </PromptInputProvider>
    </div>
  );
};
